import React, { useState } from 'react';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2 } from 'lucide-react';

const CheckoutForm = ({ packageData, onSuccess }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        setIsProcessing(true);
        setError('');

        try {
            const { error: paymentError } = await stripe.confirmPayment({
                elements,
                confirmParams: {
                    return_url: `${window.location.origin}/invoices`,
                    payment_method_data: {
                        billing_details: {
                            address: {
                                country: 'US', // or the appropriate country code
                            },
                        },
                    },
                },
            });

            if (paymentError) {
                setError(paymentError.message || 'An error occurred during payment processing.');
            } else {
                onSuccess();
            }
        } catch (err) {
            console.error('Payment error:', err);
            setError('An unexpected error occurred. Please try again.');
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
                <div className="p-6">
                    <div className="mb-4">
                        <h3 className="text-lg font-semibold">Order Summary</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                            {packageData.credits.toLocaleString()} credits
                        </p>
                    </div>

                    <div className="flex justify-between items-center mb-6">
                        <span className="text-sm font-medium">Total</span>
                        <span className="text-lg font-semibold">
                            ${packageData.discounted ?
                                packageData.discountedPrice.toFixed(2) :
                                packageData.price.toFixed(2)
                            }
                        </span>
                    </div>

                    <PaymentElement options={{
                        layout: 'tabs',
                        paymentMethodTypes: ['card'],
                        defaultValues: {
                            billingDetails: {
                                name: 'auto'
                            }
                        },
                        wallets: {
                            applePay: 'auto',
                            googlePay: 'auto'
                        }
                    }} />
                </div>
            </div>

            {error && (
                <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}

            <Button
                type="submit"
                className="w-full"
                disabled={isProcessing || !stripe || !elements}
            >
                {isProcessing ? (
                    <span className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Processing...
                    </span>
                ) : (
                    `Pay $${packageData.discounted ?
                        packageData.discountedPrice.toFixed(2) :
                        packageData.price.toFixed(2)
                    }`
                )}
            </Button>
        </form>
    );
};

export default CheckoutForm;